import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { resetPassword, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Radio } from "@phosphor-icons/react";

/**
 * Token-driven reset-password screen. Rendered when the URL contains
 * `?reset_token=...` (App.js routes the user here regardless of auth state).
 * On success the backend returns a fresh JWT so the user is signed in
 * immediately under the new password, and we redirect to `/`.
 */
export default function ResetPassword({ token }) {
  const { loginUser } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { token: jwt, user } = await resetPassword(token, pw);
      loginUser(jwt, user);
      toast.success("Password updated. You're signed in.");
      // Strip the reset_token off the URL so a refresh doesn't try to re-use it.
      window.history.replaceState({}, "", "/");
    } catch (e2) {
      const msg = formatApiError(e2?.response?.data?.detail) || "Reset failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#050505] text-white flex items-center justify-center relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent" />

      <div className="relative w-full max-w-md px-6">
        <div className="border border-white/10 bg-[#0F1115] p-6" data-testid="reset-password-panel">
          <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/10">
            <Radio size={16} className="text-red-400" />
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-400">
              SET NEW PASSWORD
            </div>
          </div>

          <p className="text-xs text-zinc-400 leading-relaxed mb-4">
            Choose a new password for your Dispatch 911 account. Once set, you'll be signed in automatically.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
                New Password
              </label>
              <Input
                data-testid="reset-new-password"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                autoComplete="new-password"
                autoFocus
                className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-red-500/40 focus-visible:border-red-500/60"
              />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
                Confirm Password
              </label>
              <Input
                data-testid="reset-confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                required
                minLength={6}
                autoComplete="new-password"
                className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-red-500/40 focus-visible:border-red-500/60"
              />
            </div>
            {err && (
              <div data-testid="reset-error" className="mono text-[10px] text-red-400 uppercase tracking-wider">
                {err}
              </div>
            )}
            <Button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-red-600 hover:bg-red-500 text-white mono uppercase tracking-widest border border-red-500 h-10"
            >
              {loading ? "Updating…" : "Set New Password →"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <a
              href="/"
              className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-300 transition-colors"
              data-testid="reset-back-link"
            >
              ← Back to sign in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
